import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Wrench, MapPin, Building2, Layers } from "lucide-react";
import { SeoMeta } from "@/components/Jsonld";
import { NEIGHBORHOODS, APPLIANCES, BRANDS } from "@/data/seo";

/**
 * Public, human-friendly directory of every SEO page in the site.
 * Mirrors the 574-URL sitemap but for humans (and search-engine link-juice).
 */
export default function Sitemap() {
  const triplePicks = [
    "samsung-refrigerator-repair-stone-oak",
    "lg-washer-repair-alamo-heights",
    "whirlpool-dryer-repair-helotes",
    "bosch-dishwasher-repair-boerne",
    "ge-oven-repair-schertz",
    "kitchenaid-dishwasher-repair-stone-oak",
    "sub-zero-refrigerator-repair-alamo-heights",
    "wolf-range-repair-stone-oak",
    "viking-range-repair-alamo-heights",
    "miele-dishwasher-repair-stone-oak",
    "thermador-range-repair-alamo-heights",
    "frigidaire-refrigerator-repair-converse",
  ];

  return (
    <div className="min-h-screen text-cream bg-navy">
      <SeoMeta
        title="Site Directory · Space Appliance Repair San Antonio"
        description="Complete directory of every appliance, brand, and neighborhood page we serve in San Antonio."
      />

      <header className="border-b border-cream/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-cream/80 hover:text-orange transition-colors" data-testid="sitemap-back">
            <ArrowLeft className="w-4 h-4" /> <span className="label-retro text-sm uppercase tracking-widest">Mission control</span>
          </Link>
          <span className="label-retro text-[11px] uppercase tracking-widest text-cream/50">Site directory</span>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 pb-8">
        <h1 className="display-retro text-cream text-4xl sm:text-5xl uppercase leading-[1.05]">
          Every page we ship.
        </h1>
        <p className="mt-4 text-cream/70 max-w-2xl leading-relaxed">
          San Antonio appliance repair, cross-indexed by appliance, neighborhood, brand, and triple combo.
          Pick a starting point — every page is local, written by hand, and routed to a tech-monaut on call.
        </p>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl">
          <Stat label="Appliance hubs" value={APPLIANCES.length} />
          <Stat label="Neighborhoods" value={NEIGHBORHOODS.length} />
          <Stat label="Brand hubs" value={BRANDS.length} />
          <Stat label="Combo pages" value="500+" />
        </div>
      </section>

      <Section icon={Wrench} title="Appliance hubs" subtitle="Every appliance we service in San Antonio.">
        <Grid>
          {APPLIANCES.map((a) => (
            <DirLink key={a.slug} to={`/services/${a.slug}`} testid={`sm-svc-${a.slug}`}>
              <span className="display-retro text-cream">{a.display}</span>
              <span className="text-cream/40 text-xs label-retro uppercase tracking-widest mt-1 block">/services/{a.slug}</span>
            </DirLink>
          ))}
        </Grid>
      </Section>

      <Section icon={MapPin} title="Neighborhoods" subtitle="Same-day windows across greater San Antonio and Bexar County.">
        <Grid>
          {NEIGHBORHOODS.map((n) => (
            <DirLink key={n.slug} to={`/locations/${n.slug}`} testid={`sm-loc-${n.slug}`}>
              <span className="display-retro text-cream">{n.name}</span>
              <span className="text-cream/40 text-xs label-retro uppercase tracking-widest mt-1 block">ZIP {n.zip} · /locations/{n.slug}</span>
            </DirLink>
          ))}
        </Grid>
      </Section>

      <Section icon={Building2} title="Brand hubs" subtitle="Factory-trained on every brand we carry parts for.">
        <Grid>
          {BRANDS.map((b) => (
            <DirLink key={b.slug} to={`/brands/${b.slug}`} testid={`sm-brand-${b.slug}`}>
              <span className="display-retro text-cream">{b.name}</span>
              <span className="text-cream/40 text-xs label-retro uppercase tracking-widest mt-1 block">{b.premium ? "Premium · " : ""}/brands/{b.slug}</span>
            </DirLink>
          ))}
        </Grid>
      </Section>

      <Section icon={Layers} title="Popular city + appliance combos" subtitle="The 18 most-requested combinations — 78 more live in the sitemap.">
        <Grid>
          {APPLIANCES.slice(0, 6).flatMap((a) =>
            NEIGHBORHOODS.slice(0, 3).map((n) => (
              <DirLink key={`${a.slug}-${n.slug}`} to={`/${a.slug}-repair-${n.slug}`} testid={`sm-combo-${a.slug}-${n.slug}`}>
                <span className="display-retro text-cream">{a.display}</span>
                <span className="text-orange text-sm mt-0.5 block">in {n.name}</span>
              </DirLink>
            ))
          )}
        </Grid>
      </Section>

      <Section icon={Layers} title="Brand × appliance × neighborhood (triple combos)" subtitle="Hand-picked triple combos. The full set of 500+ is in /api/sitemap.xml.">
        <Grid>
          {triplePicks.map((slug) => {
            const pretty = slug.replace(/-repair-/g, " repair · ").replace(/-/g, " ");
            return (
              <DirLink key={slug} to={`/${slug}`} testid={`sm-triple-${slug}`}>
                <span className="display-retro text-cream capitalize text-sm">{pretty}</span>
                <span className="text-cream/40 text-[11px] label-retro uppercase tracking-widest mt-1 block">/{slug}</span>
              </DirLink>
            );
          })}
        </Grid>
      </Section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-4 pb-20">
        <div className="bg-navy-2/60 border border-cream/15 rounded-xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="display-retro text-cream text-lg">Need the machine-readable list?</div>
            <p className="text-cream/60 text-sm mt-1">Full XML sitemap with 574 indexed URLs.</p>
          </div>
          <a
            href="/api/sitemap.xml"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sitemap-xml-link"
            className="btn-ghost label-retro text-sm self-start sm:self-auto"
          >
            View sitemap.xml <ArrowRight className="w-3.5 h-3.5 ml-1.5 inline" />
          </a>
        </div>
      </section>
    </div>
  );
}

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 border-t border-cream/10">
      <header className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-full bg-orange/15 grid place-items-center shrink-0">
          <Icon className="w-4 h-4 text-orange" />
        </span>
        <div>
          <h2 className="display-retro text-cream text-2xl sm:text-3xl uppercase tracking-tight">{title}</h2>
          {subtitle && <p className="text-cream/55 text-sm mt-1">{subtitle}</p>}
        </div>
      </header>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">{children}</div>;
}

function DirLink({ to, testid, children }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group bg-navy-2/60 border border-cream/15 hover:border-orange rounded-md px-4 py-3 flex items-start justify-between gap-3 transition-colors"
    >
      <span className="text-cream/85 group-hover:text-orange transition-colors">{children}</span>
      <ArrowRight className="w-4 h-4 text-orange shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
    </Link>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-navy-2/50 border border-cream/10 rounded-md px-3 py-2.5">
      <div className="display-retro text-cream text-xl num">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-cream/50 mt-0.5">{label}</div>
    </div>
  );
}
